import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Phone, ArrowLeft, Car, AlertCircle, CheckCircle } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const Login = () => {
  const { sendOTP, verifyOTP, loading, isAuthenticated, dummyLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('dummy'); // 'dummy' or 'firebase'
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSendOTP = async () => {
    setError('');
    if (!phone) {
      setError('Please enter your phone number');
      return;
    }
    if (mode === 'firebase') {
      const result = await sendOTP(phone);
      if (result.success) {
        setStep('otp');
      } else {
        setError(result.error || 'Failed to send OTP');
      }
    } else {
      setStep('otp'); // Dummy mode just goes to OTP input
    }
  };

  const handleVerifyOTP = async () => {
    setError('');
    if (!otp || otp.length < 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }
    if (mode === 'firebase') {
      const result = await verifyOTP(otp);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Failed to verify OTP');
      }
    } else {
      const result = await dummyLogin(phone, otp);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Invalid OTP');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <span className="w-12 h-12 text-blue-600">📱</span>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Sign in with Phone
        </h2>
        <div className="flex justify-center mt-4">
          <button
            className={`px-4 py-2 rounded-l border ${mode === 'dummy' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
            onClick={() => { setMode('dummy'); setStep('phone'); setError(''); setOtp(''); }}
          >
            Dummy OTP
          </button>
          <button
            className={`px-4 py-2 rounded-r border ${mode === 'firebase' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border-blue-600'}`}
            onClick={() => { setMode('firebase'); setStep('phone'); setError(''); setOtp(''); }}
          >
            Firebase OTP
          </button>
        </div>
      </div>
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 mt-4">
        {step === 'phone' ? (
          <div className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your phone number"
              />
              <p className="mt-2 text-sm text-gray-500">
                We'll send you a verification code via SMS
              </p>
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <button
              onClick={handleSendOTP}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                Enter OTP
              </label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={e => setOtp(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter the 6-digit OTP"
              />
              {mode === 'dummy' && (
                <p className="mt-2 text-xs text-gray-500">Use <span className="font-mono">123456</span> as the OTP for dummy login.</p>
              )}
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none"
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
        )}
        {mode === 'firebase' && <div id="recaptcha-container" />}
      </div>
    </div>
  );
};

export default Login; 